import { designerQrMinMm } from '@pz/scene';
import type { ArtworkItem, ArtworkLibrary } from '../designer-types.js';
import { el, field, note, textBtn, type DesignerCtx } from './panels.js';
import { openDialog } from './overlay.js';
import { run } from './commands.js';
import { canAddLayer, freshArtworkLayer } from './state.js';
import { fillText } from './texts.js';
import { renderDynamicShapes, renderShapeControls, renderShapeGroups } from './shape-panel.js';

/**
 * "Elementi": the drawn things — shapes first, then the QR code.
 *
 * The first row is the reference editor's "Dynamic Shapes": rectangle,
 * rounded rectangle, circle, triangle, line. They are the five a shopper
 * reaches for without browsing, so they sit above the full catalogue rather
 * than inside it. Everything else in this panel is the QR dialog, which is
 * where the reference keeps it too.
 */

export function renderElementsPanel(body: HTMLElement, ctx: DesignerCtx): () => void {
  let cancelled = false;
  const selected = ctx.selected();
  if (selected?.kind === 'shape') body.append(renderShapeControls(ctx, selected));
  if (selected?.kind === 'qr') body.append(qrSection(ctx, selected.id, selected.url));

  if (ctx.config.allow.shape) {
    const room = canAddLayer(ctx.store.state(), ctx.config, 'shape');
    if (!room.ok) {
      body.append(note(fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'warn'));
    } else {
      body.append(renderDynamicShapes(ctx));
      for (const group of renderShapeGroups(ctx)) body.append(group);
    }
  }

  // The reference keeps Graphics, Borders and Frames under Elements as short
  // rows with a "See more" into the full catalogue. Ours are the shop's own
  // picture and vector libraries; the full grid is the Ikone panel.
  const graphics = el('div');
  body.append(graphics);
  if (ctx.config.allow.artwork) void renderArtworkSections(ctx, graphics, () => cancelled);

  if (ctx.config.allow.qr) {
    const box = field('QR');
    box.wrap.append(textBtn(ctx.texts.addQr, () => openQrDialog(ctx), { icon: 'qr', className: 'pzd-command' }));
    body.append(box.wrap);
  }

  return () => {
    cancelled = true;
  };
}

/** Up to eight tiles per picture/vector library, plus a way into the rest. */
async function renderArtworkSections(
  ctx: DesignerCtx,
  host: HTMLElement,
  cancelled: () => boolean,
): Promise<void> {
  let libraries: ArtworkLibrary[];
  try {
    libraries = await ctx.artwork.libraries();
  } catch {
    return;
  }
  if (cancelled()) return;
  const wanted = libraries.filter((library) => library.kind === 'images' || library.kind === 'vectors');
  for (const library of wanted) {
    let page: { items: ArtworkItem[]; total: number };
    try {
      page = await ctx.artwork.search({ libraryId: library.id, limit: PREVIEW_TILES });
    } catch {
      continue;
    }
    if (cancelled() || !page.items.length) continue;
    const box = field(library.name);
    const grid = el('div', 'pzd-grid');
    for (const item of page.items) grid.append(artworkTile(ctx, item));
    box.wrap.append(grid);
    if (page.total > page.items.length) {
      box.wrap.append(textBtn(ctx.texts.more, () => ctx.openTool('icons'), { className: 'pzd-more' }));
    }
    host.append(box.wrap);
  }
}

const PREVIEW_TILES = 8;

function artworkTile(ctx: DesignerCtx, item: ArtworkItem): HTMLElement {
  const tile = el('button', 'pzd-tile');
  tile.type = 'button';
  tile.setAttribute('aria-label', item.name);
  tile.title = item.name;
  const picture = el('img');
  picture.src = item.url;
  picture.alt = '';
  picture.loading = 'lazy';
  tile.append(picture);
  tile.addEventListener('click', () => {
    const room = canAddLayer(ctx.store.state(), ctx.config, 'artwork');
    if (!room.ok) {
      ctx.toast(fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'error');
      return;
    }
    ctx.renderer.registerAsset(item.id, item.url);
    ctx.dispatch({
      type: 'add',
      layer: freshArtworkLayer(ctx.rawConfig, {
        assetId: item.id,
        libraryId: item.libraryId,
        vector: item.vector,
        ...(item.w ? { w: item.w } : {}),
        ...(item.h ? { h: item.h } : {}),
      }, ctx.addCount()),
    });
    ctx.refreshPanel();
  });
  return tile;
}

/** The QR controls for the code that is selected right now. */
function qrSection(ctx: DesignerCtx, id: string, url: string): HTMLElement {
  const box = field('QR');
  box.wrap.append(note(fillText(ctx.texts.qrMinSize, { n: designerQrMinMm(url) })));
  box.wrap.append(textBtn(ctx.texts.editQr, () => openQrDialog(ctx, { id, url }), { className: 'pzd-command' }));
  return box.wrap;
}

/**
 * "Dodaj QR kod" — the reference's own dialog: one URL field, the warning that
 * the shopper must scan the proof, Otkaži / Dodaj.
 */
export function openQrDialog(ctx: DesignerCtx, existing?: { id: string; url: string }): void {
  const submit = (close: () => void, url: string): void => {
    if (!run(ctx, { op: 'qr', url, ...(existing ? { id: existing.id } : {}) })) return;
    ctx.invalidate();
    close();
  };

  openDialog({
    ctx,
    title: existing ? ctx.texts.editQr : ctx.texts.addQr,
    className: 'is-qr',
    build: (body, handle) => {
      const box = field(ctx.texts.qrUrlLabel);
      const input = el('input', 'pzd-input');
      input.type = 'url';
      input.placeholder = 'https://example.com';
      input.value = existing?.url ?? '';
      input.setAttribute('aria-label', ctx.texts.qrUrlLabel);
      input.addEventListener('keydown', (event) => {
        if ((event as KeyboardEvent).key !== 'Enter') return;
        event.preventDefault();
        submit(() => handle.close(), input.value);
      });
      box.wrap.append(input);
      body.append(box.wrap, note(ctx.texts.qrScanHint));
    },
    footer: (row, handle) => {
      row.append(textBtn(ctx.texts.cancel, () => handle.close()));
      const add = el('button', 'pzd-primary', existing ? ctx.texts.editQr : ctx.texts.addQr);
      add.type = 'button';
      add.addEventListener('click', () => {
        const input = handle.element.querySelector('input');
        submit(() => handle.close(), input instanceof HTMLInputElement ? input.value : '');
      });
      row.append(add);
    },
  });
}
